import React, { Ref, forwardRef, useImperativeHandle, useRef } from "react";
import classNames from "classnames";
import {
  Button,
  IconButton,
  Icons,
  Loader,
  Text,
} from "@pushpress/shared-components";
import styles from "./DrawerPanel.module.scss";

interface DrawerPanelProps {
  title?: string;
  loading?: boolean;
  saving?: boolean;
  titlePosition?: "bottom" | "center";
  noBodyPadding?: boolean;
  onSave?: VoidFunction;
  onClose: VoidFunction;
  children?: React.ReactNode;
}

export interface DrawerPanelRef {
  scrollToTop: VoidFunction;
}

const DrawerPanel = (props: DrawerPanelProps, ref: Ref<DrawerPanelRef>) => {
  const {
    title,
    loading,
    saving,
    titlePosition = "center",
    noBodyPadding,
    onSave,
    onClose,
    children,
  } = props;

  const bodyRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToTop: () => {
        bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      },
    }),
    [],
  );

  const headerClassName = classNames(styles.header, {
    [styles.centerTitle]: titlePosition === "center",
    [styles.bottomTitle]: titlePosition === "bottom",
  });

  const bodyClassName = classNames(styles.body, {
    [styles.noBodyPadding]: noBodyPadding,
  });

  return (
    <div className={styles.root}>
      <div className={headerClassName}>
        <div className={styles.close}>
          <IconButton icon={Icons.X} onClick={onClose} color="#2A3540" />
        </div>
        {title && (
          <div className={styles.title}>
            <Text variant="heading-4" color="wolf-800" component="h2">
              {title}
            </Text>
          </div>
        )}
      </div>
      {loading ? (
        <div className={styles.loading}>
          <Loader />
        </div>
      ) : (
        <>
          <div ref={bodyRef} className={bodyClassName}>
            {children}
          </div>
          {onSave && (
            <div className={styles.footer}>
              <Button
                onClick={onSave}
                text="Save"
                buttonType="primary"
                fullWidth
                size="large"
                loading={saving}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default forwardRef(DrawerPanel);
